// MediaLibrary.tsx — browse + generate media. The customer's media/ folder in their GitHub repo
// (oriora-content-data/media/) is their library. Drop images/videos there and the brain can attach
// them. OR generate new images (BYOK DALL-E) and VIDEOS (BYOK Replicate / fal.ai / Volcengine Ark).
// Bytes never transit Oriora: previews come straight from the vendor CDN; "Save to library" runs in
// the customer's own GitHub Actions (which also measures motion — the deterministic video QA).
import { useEffect, useRef, useState } from 'react';
import { listMedia, generateImage, generateVideo, videoStatus, saveMedia, saveResult, listVideoVendors, listConnectedProviderSlugs, type MediaFile, type VideoVendor } from '../autopilot';

const IMAGE_EXTS = /\.(jpe?g|png|gif|webp|svg)$/i;
const VIDEO_EXTS = /\.(mp4|mov|webm)$/i;

function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

// A safe media/ filename from a prompt: "cozy fireplace at dusk" → cozy-fireplace-at-dusk-83731.mp4
function slugName(prompt: string, ext: string): string {
  const slug = prompt.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'generated';
  return `${slug}-${String(Date.now()).slice(-5)}.${ext}`;
}

// "Save to library" — dispatches the customer's own GitHub Action to move the bytes (vendor CDN →
// their media/ folder), then polls for the result JSON (which carries the motion-meter QA reading).
function SaveToLibrary({ url, suggestName, onSaved }: { url: string; suggestName: string; onSaved: () => void }) {
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');
  const [detail, setDetail] = useState('');
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);

  async function save() {
    setState('saving'); setDetail('dispatching to your GitHub Actions…');
    try {
      const { dispatch_id } = await saveMedia(url, suggestName);
      let ticks = 0;
      timer.current = setInterval(async () => {
        ticks++;
        try {
          const r = await saveResult(dispatch_id);
          if (r.status === 'saved') {
            if (timer.current) clearInterval(timer.current);
            setState('saved');
            setDetail(`${r.path}${typeof r.motion === 'number' && r.motion > 0 ? ` · motion ${r.motion.toFixed(2)}${r.motion <= 0.2 ? ' (calm)' : r.motion > 0.5 ? ' (fast)' : ''}` : ''}`);
            onSaved();
          } else if (ticks > 36) { // ~3 min
            if (timer.current) clearInterval(timer.current);
            setState('failed'); setDetail('Timed out — check the Actions tab of your oriora-content-data repo.');
          }
        } catch { /* keep polling */ }
      }, 5000);
    } catch (e) { setState('failed'); setDetail(String((e as Error).message ?? e)); }
  }

  if (state === 'saved') return <p className="gen-caption">✅ Saved to {detail}</p>;
  return (
    <p className="gen-caption">
      <button className="btn btn-sm" onClick={() => void save()} disabled={state === 'saving'}>
        {state === 'saving' ? 'Saving…' : 'Save to library'}
      </button>
      {state === 'saving' && <span className="muted"> {detail}</span>}
      {state === 'failed' && <span className="error-line"> {detail}</span>}
      {' '}<a href={url} target="_blank" rel="noreferrer">open ↗</a>
    </p>
  );
}

// "Generate a video" — vendor (BYOK key from the website wizard) + prompt + optional library image
// to animate (image→video). Submit returns a task id; we poll until the vendor CDN URL arrives and
// preview it straight from there. The billed part is the ONE submit; polling is free.
function GenerateVideoPanel({ imageFiles, onSaved }: { imageFiles: MediaFile[]; onSaved: () => void }) {
  const [vendors, setVendors] = useState<VideoVendor[]>([]);
  const [vendor, setVendor] = useState('');
  const [vPrompt, setVPrompt] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState('');
  const [err, setErr] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [connected, setConnected] = useState<string[]>([]);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    // Preselect a vendor the customer actually has a key for — defaulting to the first row sent
    // key-less customers into "connect your Replicate key" even when their Ark key was connected.
    Promise.all([listVideoVendors(), listConnectedProviderSlugs()]).then(([v, slugs]) => {
      setVendors(v); setConnected(slugs);
      const ready = v.find((x) => slugs.includes(x.vendor));
      if (ready) setVendor(ready.vendor); else if (v[0]) setVendor(v[0].vendor);
    });
  }, []);
  useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);

  async function generate() {
    if (!vPrompt.trim() || !vendor) return;
    setBusy(true); setErr(''); setVideoUrl(''); setPhase('submitting…');
    try {
      const { task_id, model } = await generateVideo({ vendor, prompt: vPrompt.trim(), image_url: imageUrl || undefined, resolution: '720p' });
      setPhase('rendering on your vendor account (typically 1–3 min)…');
      let ticks = 0;
      timer.current = setInterval(async () => {
        ticks++;
        try {
          const s = await videoStatus(vendor, task_id, model);
          if (s.status === 'succeeded' && s.video_url) {
            if (timer.current) clearInterval(timer.current);
            setVideoUrl(s.video_url); setPhase(''); setBusy(false);
          } else if (s.status === 'failed') {
            if (timer.current) clearInterval(timer.current);
            setErr(s.error || 'Generation failed on the vendor side.'); setPhase(''); setBusy(false);
          } else if (ticks > 60) { // ~6 min
            if (timer.current) clearInterval(timer.current);
            setErr('Timed out waiting for the vendor — try again or check your vendor dashboard.'); setPhase(''); setBusy(false);
          }
        } catch { /* transient poll error → keep polling */ }
      }, 6000);
    } catch (e) { setErr(String((e as Error).message ?? e)); setPhase(''); setBusy(false); }
  }

  return (
    <div className="media-gen-panel">
      <h3 className="panel-label">Generate a video</h3>
      <div className="gen-row">
        <select className="gen-input" style={{ maxWidth: 220, flex: 'none' }} value={vendor} onChange={(e) => setVendor(e.target.value)}>
          {vendors.length === 0 && <option value="">(loading vendors…)</option>}
          {vendors.map((v) => <option key={v.vendor} value={v.vendor}>{v.displayName}{connected.includes(v.vendor) ? ' ✓' : ''}</option>)}
        </select>
        <input
          className="gen-input"
          style={{ minWidth: 220 }}
          placeholder="Describe the video — e.g. 'flames flickering gently in a stone fireplace, cozy, static camera'"
          value={vPrompt}
          onChange={(e) => setVPrompt(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !busy && void generate()}
        />
        <button className="btn" onClick={() => void generate()} disabled={busy || !vPrompt.trim() || !vendor}>
          {busy ? 'Working…' : 'Generate'}
        </button>
      </div>
      {imageFiles.length > 0 && (
        <div className="gen-row" style={{ marginTop: 6 }}>
          <select className="gen-input" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)}>
            <option value="">Text → video (no source image)</option>
            {imageFiles.map((f) => <option key={f.path} value={f.download_url!}>Animate: {f.name}</option>)}
          </select>
        </div>
      )}
      {phase && <p className="muted">{phase}</p>}
      {err && <p className="error-line">{err}{err.includes('key') && <> — <a href="https://orioralabs.com/settings?tab=keys" target="_blank" rel="noreferrer">connect a video key ↗</a></>}</p>}
      {videoUrl && (
        <div className="gen-result">
          <video src={videoUrl} controls loop className="gen-preview" />
          <SaveToLibrary url={videoUrl} suggestName={slugName(vPrompt, 'mp4')} onSaved={onSaved} />
        </div>
      )}
    </div>
  );
}

export default function MediaLibrary() {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [prompt, setPrompt] = useState('');
  const [genBusy, setGenBusy] = useState(false);
  const [genResult, setGenResult] = useState<{ image_url: string; prompt: string } | null>(null);
  const [genError, setGenError] = useState('');

  function refresh() {
    listMedia()
      .then(setFiles)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }
  useEffect(refresh, []);

  async function generate() {
    if (!prompt.trim()) return;
    setGenBusy(true); setGenError(''); setGenResult(null);
    try {
      const r = await generateImage(prompt.trim());
      setGenResult(r);
    } catch (e) { setGenError(String(e)); }
    finally { setGenBusy(false); }
  }

  return (
    <div className="media-library">
      <div className="section-header">
        <h2 className="section-title">Media library</h2>
        <p className="section-sub">Your <code>media/</code> folder in <code>oriora-content-data</code>. Drop images or videos there and they appear here — the brain can attach them to posts.</p>
      </div>

      <div className="media-gen-panel">
        <h3 className="panel-label">Generate an image</h3>
        <div className="gen-row">
          <input
            className="gen-input"
            placeholder="Describe the image you want…"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !genBusy && void generate()}
          />
          <button className="btn" onClick={() => void generate()} disabled={genBusy || !prompt.trim()}>
            {genBusy ? 'Generating…' : 'Generate'}
          </button>
        </div>
        {genError && <p className="error-line">{genError}</p>}
        {genResult && (
          <div className="gen-result">
            <img src={genResult.image_url} alt={genResult.prompt} className="gen-preview" />
            <p className="gen-caption">"{genResult.prompt}"</p>
            <SaveToLibrary url={genResult.image_url} suggestName={slugName(genResult.prompt, 'png')} onSaved={refresh} />
          </div>
        )}
      </div>

      <GenerateVideoPanel imageFiles={files.filter((f) => IMAGE_EXTS.test(f.name) && f.download_url)} onSaved={refresh} />

      <div className="media-grid-section">
        <h3 className="panel-label">Your media ({files.length} files)</h3>
        {loading && <p className="muted">Loading…</p>}
        {error && <p className="error-line">{error}</p>}
        {!loading && !error && files.length === 0 && (
          <p className="muted">No media yet. Drop images or videos into <code>oriora-content-data/media/</code> in your GitHub repo, then refresh.</p>
        )}
        <div className="media-grid">
          {files.map((f) => (
            <div key={f.path} className="media-card">
              {f.download_url && IMAGE_EXTS.test(f.name) && (
                <img src={f.download_url} alt={f.name} className="media-thumb" loading="lazy" />
              )}
              {VIDEO_EXTS.test(f.name) && (
                <div className="media-thumb media-thumb-video">▶ video</div>
              )}
              {!IMAGE_EXTS.test(f.name) && !VIDEO_EXTS.test(f.name) && (
                <div className="media-thumb media-thumb-file">📄</div>
              )}
              <div className="media-info">
                <span className="media-name" title={f.name}>{f.name}</span>
                <span className="media-size">{fileSize(f.size)}</span>
              </div>
              {f.download_url && (
                <a className="media-open" href={f.download_url} target="_blank" rel="noreferrer">open ↗</a>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
